import { useState } from 'react';

import { Button } from '@components/button';
import { FormLabel } from '@components/form-label';
import { IconWorkType } from '@components/icons/work-type';
import { Sidebar, useSidebar } from '@components/sidebar';
import { CloseButton, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';

import { useCreateWorkTypes, useUpdateWorkTypes } from 'api/worktypes/hooks';
import { Translation, WorkType } from 'api/worktypes/requests';
import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { useStore } from 'models/Provider';

import locale from './locale.json';

export const useWorkTypeSidebar = () =>
  useSidebar(WorkTypeSidebar, {
    variant: 'LARGE',
  });

const WorkTypeSidebar = ({ workType }: { workType?: WorkType }) => {
  const isEdit = Boolean(workType);
  const { t } = useScopedTranslate(locale);
  const { current } = useStore();
  const [translations, setTranslations] = useState<Translation[]>(
    current.langs.map((lang: CareerSiteLanguageType) => ({
      language: lang,
      title:
        workType?.translations?.find((translation) => translation.language === lang)?.title ?? '',
    }))
  );
  const [enabled, setEnabled] = useState(workType?.enabled ?? true);
  const [languageError, setLanguageError] = useState<{ language?: string; message?: string }>({});
  const onSuccess = async (workTypeId: number) => {
    await current.loadInfo();
    hideSidebar();
  };
  const createMutation = useCreateWorkTypes({ onSuccess, setLanguageError });
  const updateMutation = useUpdateWorkTypes({ onSuccess, setLanguageError });

  const setValue = (title: string, language: CareerSiteLanguageType) => {
    setTranslations((translation) =>
      translation.map((lang) => ({
        ...lang,
        title: lang.language === language ? title : lang.title,
      }))
    );
  };

  const handleSave = () => {
    const data = {
      enabled,
      translations,
    };
    if (workType) {
      updateMutation.mutate({ ...data, id: workType.id });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack space={4}>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack space={2} orientation="horizontal" align="center">
              <IconWorkType color="blue100" />
              <Sidebar.HeaderTitle title={isEdit ? t('editWorkType') : t('addWorkType')} />
            </Stack>
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={4}>
            {current.langs.map((lang: CareerSiteLanguageType) => (
              <Stack key={lang} space={1}>
                <TextField
                  id={`edit-worktype-${lang}`}
                  labelIcon={FLAG_MAPPER_SMALL[lang]}
                  label={t('workTypeName', { lang: lang.toUpperCase() })}
                  onChange={(title) => setValue(title, lang)}
                  value={translations?.find((translation) => translation.language === lang)?.title}
                  error={languageError.language === lang ? languageError.message : undefined}
                />
              </Stack>
            ))}

            <Stack orientation="horizontal" justify="space-between" align="center">
              <Stack>
                <FormLabel text={t('workType')} />
                <Text
                  text={enabled ? t('enabled') : t('disabled')}
                  color="purple50"
                  font="regular14"
                />
              </Stack>

              <Switch
                label=""
                labelVisible={false}
                defaultValue={enabled}
                onChange={(value) => setEnabled(value)}
              />
            </Stack>
          </Stack>
          <Stack orientation="horizontal" space={4} justify="flex-end">
            <Button variant="secondary" onClick={hideSidebar} text={t('cancel')} />
            <Button
              isDisabled={translations.some((translation) => !translation.title)}
              onClick={() => handleSave()}
              variant="primary-blue"
              text={t('save')}
            />
          </Stack>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};
